import { hasValue } from '@celum/core/util';

import { getTokenForId } from './get-token-for-id';
import { AccountMember } from '../model/account-member.model';
import { AccountAccess, AccountAccessStatus, AccountMemberRole, InvitationStatus, TrialPlan, UserDetails } from '../model/user.model';

export class AccountUtil {
  public static getAccountAccessOrTrialPlanId(account: AccountAccess | TrialPlan): string {
    return (account as AccountAccess)?.accountId || (account as TrialPlan)?.accountRequestId;
  }

  public static getAccountAccessOrTrialPlanName(account: AccountAccess | TrialPlan): string {
    return (account as AccountAccess)?.accountName || (account as TrialPlan)?.accountRequestOrgName;
  }

  /**
   * @deprecated Will be removed once all applications switched to cookie-based authentication for avatars/logos
   */
  public static getAccountLogo(accountLogoDownloadLink: string, accountAccessToken?: string): string {
    if (!accountLogoDownloadLink) {
      return undefined;
    }

    return accountAccessToken ? `${accountLogoDownloadLink}&token=${accountAccessToken}` : accountLogoDownloadLink;
  }

  public static getAccountInfo(account: AccountAccess | TrialPlan): { id: string; name: string; logo?: string } {
    return {
      id: AccountUtil.getAccountAccessOrTrialPlanId(account),
      name: AccountUtil.getAccountAccessOrTrialPlanName(account),
      logo: isAccountAccess(account)
        ? AccountUtil.getAccountLogo(account.accountLogoDownloadLink, getTokenForId(account.accountId, account.accountAccessToken))
        : undefined
    };
  }

  public static getAllAdminAccountAccessesForUser(currentUser: UserDetails): AccountAccess[] {
    const accountAccesses = AccountUtil.getUserAccountAccesses(currentUser);
    return accountAccesses.filter(AccountUtil.isActiveAccountAdmin);
  }

  public static getAccountsAndTrialPlanForUser(currentUser: UserDetails): (AccountAccess | TrialPlan)[] {
    const accountAccesses = AccountUtil.getUserAccountAccesses(currentUser);
    return [...accountAccesses, currentUser.trialPlan].filter(account => account && AccountUtil.getAccountAccessOrTrialPlanId(account));
  }

  public static getActiveAccountsForUser(currentUser: UserDetails): AccountAccess[] {
    const accountAccesses = AccountUtil.getUserAccountAccesses(currentUser);
    return accountAccesses.filter(accountAccess => accountAccess.status === AccountAccessStatus.ACTIVE);
  }

  public static getManagedAccountOrTrialPlanById(currentUser: UserDetails, activeId: string): AccountAccess | TrialPlan {
    const accountAccesses = AccountUtil.getAccountsAndTrialPlanForUser(currentUser);
    return accountAccesses.find(account => account && AccountUtil.getAccountAccessOrTrialPlanId(account) === activeId);
  }

  public static getAccountOrTrialPlanById(accountAccesses: AccountAccess[], activeId: string): AccountAccess;
  public static getAccountOrTrialPlanById(currentUser: UserDetails, activeId: string): AccountAccess | TrialPlan;
  public static getAccountOrTrialPlanById(userOrAccounts: UserDetails | AccountAccess[], activeId: string): AccountAccess | TrialPlan {
    const accountAccesses = isUserDetails(userOrAccounts)
      ? [...AccountUtil.getActiveAccountsForUser(userOrAccounts), userOrAccounts.trialPlan]
      : userOrAccounts;
    return accountAccesses.find(account => account && AccountUtil.getAccountAccessOrTrialPlanId(account) === activeId);
  }

  public static getUserAccountAccesses(currentUser: UserDetails): AccountAccess[] {
    return currentUser?.accountAccesses || [];
  }

  public static isActiveAccountAdmin(accAccess: AccountAccess): boolean {
    return accAccess && accAccess.status === AccountAccessStatus.ACTIVE && accAccess.effectiveRole === AccountMemberRole.ACCOUNT_ADMIN;
  }

  public static isActiveAccountAdminForAtLeastOneAccountAccess(accountAccesses: AccountAccess[]): boolean {
    return hasValue(accountAccesses) && accountAccesses.some(accountAccess => AccountUtil.isActiveAccountAdmin(accountAccess));
  }

  public static accountOwner(email: string, accAccess: AccountAccess): boolean {
    return accAccess && accAccess.ownerEmail === email;
  }
}

function isUserDetails(userDetails: UserDetails | any): userDetails is UserDetails {
  return (userDetails as UserDetails).email !== undefined;
}

function isAccountAccess(account: any): account is AccountAccess {
  return account && typeof account.accountId === 'string';
}

export function isInvitee(user: AccountMember): boolean {
  return user.invitationStatus === InvitationStatus.INVITED || user.invitationStatus === InvitationStatus.REJECTED;
}
